
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserPlus } from "lucide-react";
import { BrandRole } from "@/hooks/useBrandTeam";
import { useCustomRoles } from "@/hooks/useCustomRoles";
import { useTypedTranslation } from "@/lib/i18n/hooks";

interface TeamInvitationProps {
  onInviteMember: (email: string, role: BrandRole, customRoleId?: string, createSquadMember?: boolean, squadType?: string) => Promise<boolean>;
  isInviting: boolean;
  brandId?: string;
}

const TeamInvitation = ({ onInviteMember, isInviting, brandId }: TeamInvitationProps) => {
  const { t } = useTypedTranslation();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<BrandRole>("viewer");
  const [selectedCustomRoleId, setSelectedCustomRoleId] = useState<string>("none");
  const [createSquadMember, setCreateSquadMember] = useState(false);
  const [squadType, setSquadType] = useState<string>("Growth Partner");
  const { roles: customRoles } = useCustomRoles(brandId);

  const squadTypes = ["Growth Partner", "Client", "Third Party"];

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) return;

    const customRoleIdToPass = selectedCustomRoleId === "none" ? undefined : selectedCustomRoleId;
    const success = await onInviteMember(
      inviteEmail, 
      inviteRole, 
      customRoleIdToPass,
      createSquadMember,
      createSquadMember ? squadType : undefined
    );
    if (success) {
      setInviteEmail("");
      setInviteRole("viewer");
      setSelectedCustomRoleId("none");
      setCreateSquadMember(false);
      setSquadType("Growth Partner");
    }
  };

  return (
    <Card className="bg-muted/50 dark:bg-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserPlus className="h-5 w-5" />
          {t('brandSetup.brandTeam.invitation.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex gap-4">
            <Input
              placeholder={t('brandSetup.brandTeam.invitation.emailPlaceholder')}
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="flex-1"
              type="email"
            />
            <Select value={inviteRole} onValueChange={(value) => setInviteRole(value as BrandRole)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder={t('brandSetup.brandTeam.invitation.userRole')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="viewer">{t('brandSetup.brandTeam.roles.viewer')}</SelectItem>
                <SelectItem value="member">{t('brandSetup.brandTeam.roles.member')}</SelectItem>
                <SelectItem value="admin">{t('brandSetup.brandTeam.roles.admin')}</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              onClick={handleInviteMember}
              disabled={!inviteEmail.trim() || isInviting}
            >
              {isInviting ? t('brandSetup.brandTeam.invitation.inviting') : t('brandSetup.brandTeam.invitation.invite')}
            </Button>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="growth-squad"
                checked={createSquadMember}
                onCheckedChange={(checked) => setCreateSquadMember(checked === true)}
              />
              <label htmlFor="growth-squad" className="text-sm font-medium">
                {t('brandSetup.brandTeam.invitation.growthSquadMember')}
              </label>
            </div>

            {createSquadMember && (
              <div className="flex-1">
                <Select value={squadType} onValueChange={setSquadType}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('brandSetup.brandTeam.invitation.selectSquadType')} />
                  </SelectTrigger>
                  <SelectContent>
                    {squadTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {customRoles.length > 0 && (
            <div>
              <label className="text-sm font-medium mb-2 block">{t('brandSetup.brandTeam.invitation.jobRole')}</label>
              <Select value={selectedCustomRoleId} onValueChange={setSelectedCustomRoleId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('brandSetup.brandTeam.invitation.selectJobRole')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('brandSetup.brandTeam.invitation.noJobRole')}</SelectItem>
                  {customRoles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{role.name}</span>
                        {role.description && (
                          <span className="text-sm text-muted-foreground">{role.description}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="text-sm text-muted-foreground space-y-1">
            <p><strong>{t('brandSetup.brandTeam.invitation.userRoles')}</strong></p>
            <p><strong>{t('brandSetup.brandTeam.roles.viewer')}:</strong> {t('brandSetup.brandTeam.invitation.viewerDescription')}</p>
            <p><strong>{t('brandSetup.brandTeam.roles.member')}:</strong> {t('brandSetup.brandTeam.invitation.memberDescription')}</p>
            <p><strong>{t('brandSetup.brandTeam.roles.admin')}:</strong> {t('brandSetup.brandTeam.invitation.adminDescription')}</p>
            {selectedCustomRoleId && selectedCustomRoleId !== "none" && (
              <div className="mt-2 p-2 bg-muted rounded">
                <p><strong>{t('brandSetup.brandTeam.invitation.jobRoleLabel')}</strong> {customRoles.find(r => r.id === selectedCustomRoleId)?.name}</p>
                <p className="text-xs">
                  {customRoles.find(r => r.id === selectedCustomRoleId)?.permissions.length} {t('brandSetup.brandTeam.invitation.menuPermissionsAssigned')}
                </p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TeamInvitation;
